import { useState, useEffect, useRef, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { showMlSheetAutoSyncAlerts } from '../../utils/mlSheetAutoSyncAlerts.js';
import axios from "../../config/axios"
import { Plus, Package, ChevronLeft, ChevronRight, XCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import Button from '../common/Button.js';
import { hasInternalHubDispatchControls, hasShipmentCrossingAccess, isMlAccessUsersRequired } from '../../utils/networkMode.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import CreateVehicleWithOwnerModal from './CreateVehicleWithOwnerModal.js';
import MLSheetShipmentRecordList from './MLSheetShipmentRecordList.js';
import MlAccessAgentChips from './MlAccessAgentChips.js';
import MlLocationChips from './MlLocationChips.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { enrichVehiclesWithOwners, formatVehicleLabel } from '../../utils/vehicleOwnerDisplay.js';
import { groupAccessAgents, formatAccessAgentMeta, matchedAccessUserIds } from '../../utils/mlSheetAccessAgentList.js';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import { evaluateVehicleLoad, formatKg } from '../../utils/vehicleLoad.js';
import {
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_FIELD_LABEL,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';
import routeService from '../../services/RouteService.js';
import {
  buildLocationHubParentMap,
  crossingStopsFromFlat,
  formatCrossingStopSummary,
  mlFormFieldsFromMatchedRoute,
  ML_ROUTE_LOCATION_MISMATCH,
  resolveMatchedRouteForMlForm,
  routeMatchesMlFromTo,
} from '../../utils/routeDisplay.js';
import { applySelectedRouteToMlForm, syncMlRouteSelection } from '../../utils/mlRouteFormHelpers.js';
import { filterShipmentsForMlDispatchForm } from '../../utils/mlDispatchShipmentFilter.js';
import MlRouteSelectField from './MlRouteSelectField.js';
import {
  dropDispatchLocationsWithoutShipments,
  mlLocationChipCounts,
  replaceDispatchLocationsIfChanged,
} from '../../utils/mlRouteStopCounts.js';
import { useTranslation } from 'react-i18next';

const sanitizeDriverContact = (value) => String(value || "").replace(/\D/g, "").slice(0, 10)
const isValidDriverContact = (value) => /^\d{10}$/.test(String(value || ""))

const MOBILE_STEPS = [
  { id: "filters", label: "Filters" },
  { id: "vehicle", label: "Vehicle" },
  { id: "shipments", label: "Shipments" },
]


const EditMLSheet = () => {
  const { t } = useTranslation()
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const accessUsersRequired = isMlAccessUsersRequired(user)
  const canSelectCrossingHubs = hasShipmentCrossingAccess(user)
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const userRole = user?.role || user?.role_name
  const lockFromLocation = ['Agent', 'Hub Admin'].includes(userRole)
  const fromLocationLocked = lockFromLocation
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [pendingVehicleSearch, setPendingVehicleSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [searchingShipments, setSearchingShipments] = useState(false)
  const [agents, setAgents] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [locations, setLocations] = useState([])
  const [mlSheet, setMLSheet] = useState(null)
  const [filteredShipments, setFilteredShipments] = useState([])
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([])
  const [allShipments, setAllShipments] = useState([])
  const [loadedSelectedShipmentData, setLoadedSelectedShipmentData] = useState(false)
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  const [formData, setFormData] = useState({
      from_location_ids: [],
      to_location_ids: [],
      crossing_location_hub_ids: [],
      crossing_destination_hub_ids: [],
      crossing_stops: [],
      crossingStopsFromRoute: false,
      from_date: "",
      to_date: "",
      vehicle_id: "",
      driver_contact: "",
      access_user_ids: [],
      route_id: "",
    })
  const [routes, setRoutes] = useState([])
  const hubParentMap = useMemo(() => buildLocationHubParentMap(locations), [locations])
  const routeLocationWarning = useMemo(() => {
    if (!formData.route_id || !formData.from_location_ids.length || !formData.to_location_ids.length) {
      return '';
    }
    const route = routes.find((r) => String(r.id) === String(formData.route_id));
    if (!route) return '';
    if (!routeMatchesMlFromTo(route, formData.from_location_ids, formData.to_location_ids, hubParentMap)) {
      return ML_ROUTE_LOCATION_MISMATCH;
    }
    return '';
  }, [
    formData.route_id,
    formData.from_location_ids,
    formData.to_location_ids,
    routes,
    hubParentMap,
  ])
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchInputs, setSearchInputs] = useState({
    from: "",
    to: "",
    crossing: "",
    crossingDest: "",
    vehicle: "",
    access: "",
    route: "",
  })
  const fromRef = useRef(null)
  const toRef = useRef(null)
  const crossingRef = useRef(null)
  const crossingDestRef = useRef(null)
  const [savedCrossingHubIds, setSavedCrossingHubIds] = useState([])
  const [crossingDestUnset, setCrossingDestUnset] = useState(true)
  const vehicleRef = useRef(null)
  const accessRef = useRef(null)
  const routeRef = useRef(null)
  const syncedToLocationIdsRef = useRef(null)

  const [mobileStep, setMobileStep] = useState(0)
  const [alreadyLoadedKg, setAlreadyLoadedKg] = useState(0)
  const mobileStepRef = useRef(0)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  )

  useEffect(() => {
    mobileStepRef.current = mobileStep
  }, [mobileStep])

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !fromRef.current?.contains(e.target) &&
        !toRef.current?.contains(e.target) &&
        !crossingRef.current?.contains(e.target) &&
        !crossingDestRef.current?.contains(e.target) &&
        !vehicleRef.current?.contains(e.target) &&
        !accessRef.current?.contains(e.target) &&
        !routeRef.current?.contains(e.target)
      ) {
        setOpenDropdown(null)
        setSearchInputs({
          from: "",
          to: "",
          crossing: "",
          crossingDest: "",
          vehicle: "",
          access: "",
          route: "",
        })
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    if (mobileStep !== step) return "hidden"
    return step === MOBILE_STEPS.length - 1
      ? "flex flex-col flex-1 min-h-0 overflow-hidden pb-4"
      : "flex flex-col flex-1 min-h-0 overflow-y-auto"
  }

  const validateMobileStep = (step) => {
    if (step === 0) {
      if (formData.from_location_ids.length === 0) {
        Alert.error("Please select at least one from location")
        return false
      }
      if (formData.to_location_ids.length === 0) {
        Alert.error("Please select at least one to location")
        return false
      }
      return true
    }
    if (step === 1) {
      if (!formData.vehicle_id) {
        Alert.error("Please select a vehicle")
        return false
      }
      if (!isValidDriverContact(formData.driver_contact)) {
        Alert.error("Driver contact must be 10 digits")
        return false
      }
      if (accessUsersRequired && formData.access_user_ids.length === 0) {
        Alert.error("Please select at least one access user")
        return false
      }
      if (accessUsersRequired && formData.access_user_ids.includes(user?.user_id)) {
        Alert.error("You cannot assign the ML sheet to yourself. Select another access user.")
        return false
      }
      return true
    }
    return true
  }

  const goToNextMobileStep = () => {
    const step = mobileStepRef.current
    if (!validateMobileStep(step)) return
    const nextStep = Math.min(step + 1, MOBILE_STEPS.length - 1)
    mobileStepRef.current = nextStep
    setMobileStep(nextStep)
  }

  const goToPrevMobileStep = () => {
    setMobileStep((s) => {
      const prev = Math.max(s - 1, 0)
      mobileStepRef.current = prev
      return prev
    })
  }

  const handleMobileStepTabClick = (idx) => {
    const current = mobileStepRef.current
    if (idx === current) return
    if (idx < current) {
      mobileStepRef.current = idx
      setMobileStep(idx)
      return
    }
    for (let step = current; step < idx; step += 1) {
      if (!validateMobileStep(step)) return
    }
    mobileStepRef.current = idx
    setMobileStep(idx)
  }

  const handleMobilePrimaryAction = () => {
    if (mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }
    handleSubmit({ preventDefault: () => {} })
  }

  useEffect(() => {
    fetchMLSheet()
  }, [mlSheetId])

  useEffect(() => {
    let cancelled = false
    const vehicleId = formData.vehicle_id
    if (!vehicleId || !user?.is_cargo_shipment) {
      setAlreadyLoadedKg(0)
      return undefined
    }
    const created = mlSheet?.created_at ? String(mlSheet.created_at).slice(0, 10) : undefined
    ;(async () => {
      try {
        const res = await mlSheetService.getVehicleDayCapacity({
          vehicleId,
          date: created,
          excludeMlSheetId: mlSheetId,
        })
        const kg = res?.data?.data?.already_loaded_kg
        if (!cancelled) setAlreadyLoadedKg(Number(kg) || 0)
      } catch {
        if (!cancelled) setAlreadyLoadedKg(0)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [formData.vehicle_id, user?.is_cargo_shipment, mlSheetId, mlSheet?.created_at])

  useEffect(() => {
      searchShipments()
    }, [
      formData.from_location_ids,
      formData.to_location_ids,
      formData.from_date,
      formData.to_date,
      formData.route_id,
    ])

  useEffect(() => {
    const from = formData.from_location_ids
    const to = formData.to_location_ids
    if (from.length === 0 || to.length === 0) {
      setFormData((prev) => (prev.route_id ? mlFormFieldsFromMatchedRoute(prev, null) : prev))
      return undefined
    }
    let cancelled = false
    resolveMatchedRouteForMlForm(routeService, {
      routes,
      fromIds: from,
      toIds: to,
      parentMap: hubParentMap,
    }).then((matched) => {
      if (cancelled) return
      setFormData((prev) => replaceDispatchLocationsIfChanged(
        prev,
        dropDispatchLocationsWithoutShipments(
          syncMlRouteSelection(prev, {
            matched,
            routes,
            parentMap: hubParentMap,
          }),
          locationChipCountsRef.current,
        ),
      ))
      if (matched?.id) {
        setRoutes((prev) => {
          const idx = prev.findIndex((r) => String(r.id) === String(matched.id))
          if (idx === -1) return [...prev, matched]
          // Prefer API match payload over thin list rows so From/To validation
          // sees via/crossing hubs and does not flash a false mismatch warning.
          const next = [...prev]
          next[idx] = { ...prev[idx], ...matched }
          return next
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [formData.from_location_ids, formData.to_location_ids, routes, hubParentMap])

  const fetchSelectedShipmentsData = async (shipmentIds) => {
    if (!shipmentIds || shipmentIds.length === 0) {
      setLoadedSelectedShipmentData(true)
      return
    }
  
    try {
      // Fetch details for each selected shipment from the ML sheet
      const shipmentDetailPromises = shipmentIds.map((id) =>
        axios
          .get(`/shipments/${id}`)
          .then((res) => res.data.shipment || res.data.data || res.data)
          .catch((err) => {
            console.error(`Failed to fetch shipment ${id}:`, err)
            return { shipment_id: id, id: id, isMinimal: true }
          })
      )
  
      const shipmentDetails = await Promise.all(shipmentDetailPromises)
      const validShipments = shipmentDetails.filter(Boolean)
      
      setAllShipments(validShipments)
      setFilteredShipments([]) // Clear filtered since we're showing existing shipments
      setLoadedSelectedShipmentData(true)
    } catch (error) {
      console.error("Error fetching selected shipments data:", error)
      setLoadedSelectedShipmentData(true)
    }
  }

  const fetchMLSheet = async () => {
    try {
      setLoading(true)
      const [mlRes, agentsRes, vehiclesRes, locationsRes, ownersRes, routesRes] = await Promise.all([
        mlSheetService.getMLSheet(mlSheetId),
        mlSheetService.getAgents(),
        mlSheetService.getAllVehiclesNoPage(),
        mlSheetService.getLocations(),
        vehicleOwnerService.getAll(1, 500).catch(() => ({ data: [] })),
        routeService.getAll(1, 1000).catch(() => ({ data: [] })),
      ])

      const mlData = mlRes.data.ml_sheet || mlRes.data.data
      setMLSheet(mlData)

      const agentsData = agentsRes.data.data || agentsRes.data.users || agentsRes.data || []
      const vehiclesData = vehiclesRes.data.data || vehiclesRes.data.vehicles || vehiclesRes.data || []
      const ownersData = ownersRes?.data || ownersRes || []
      const locationsData = locationsRes.data.hubs || locationsRes.data.data || locationsRes.data || []

      setAgents(Array.isArray(agentsData) ? agentsData : [])
      setVehicles(
        enrichVehiclesWithOwners(
          Array.isArray(vehiclesData) ? vehiclesData : [],
          Array.isArray(ownersData) ? ownersData : []
        )
      )
      setLocations(Array.isArray(locationsData) ? locationsData : [])
      const routesData = Array.isArray(routesRes?.data)
        ? routesRes.data
        : (routesRes?.data?.data || [])
      setRoutes(Array.isArray(routesData) ? routesData : [])

      const shipmentIds = mlData.shipment_ids || []
      setSelectedShipmentIds(shipmentIds)

      fetchSelectedShipmentsData(shipmentIds)

      // Extract location names from the ML Sheet data
      const fromLocationIds = mlData.from_location_ids || []
      const toLocationIds = mlData.to_location_ids || []
      syncedToLocationIdsRef.current = toLocationIds.map(String)
      const crossingLocationHubIds = mlData.crossing_location_hub_ids || []
      const crossingDestinationHubIds = mlData.crossing_destination_hub_ids || []
      setSavedCrossingHubIds(crossingLocationHubIds)
      setCrossingDestUnset(mlData.crossing_destination_hub_ids == null)

      setFormData({
        from_location_ids: fromLocationIds,
        to_location_ids: toLocationIds,
        crossing_location_hub_ids: crossingLocationHubIds,
        crossing_destination_hub_ids: crossingDestinationHubIds,
        crossing_stops: mlData.crossing_stops || [],
        crossingStopsFromRoute: Array.isArray(mlData.crossing_stops) && mlData.crossing_stops.length > 0,
        from_date: "",
        to_date: "",
        vehicle_id: mlData.vehicle_id,
        driver_contact: sanitizeDriverContact(mlData.driver_contact),
        access_user_ids: mlData.access_users?.map((u) => u.user_id || u.id || u) || [],
        route_id: mlData.route_id || mlData.route?.id || "",
      })
    } catch (error) {
      console.error("Error fetching ML Sheet:", error)
      Alert.error("Failed to load ML Sheet")
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  const searchShipments = async () => {
    if (
      formData.from_location_ids.length === 0
      || formData.to_location_ids.length === 0
      || !formData.route_id
    ) {
      setFilteredShipments([])
      setSelectAllChecked(false)
      return
    }
  
    try {
      setSearchingShipments(true)
  
      const searchParams = {}
      const findLocById = (id) =>
        locations.find((l) => String(l.id || l.hub_id) === String(id))

      if (formData.from_location_ids.length > 0) {
        searchParams.from_location_ids = formData.from_location_ids.join(",")
        const fromLabels = formData.from_location_ids
          .map((id) => {
            const loc = findLocById(id)
            return loc ? formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (fromLabels.length > 0) {
          searchParams.from_location = fromLabels.join(",")
        }
      }

      if (formData.to_location_ids.length > 0) {
        searchParams.to_location_ids = formData.to_location_ids.join(",")
        const toLabels = formData.to_location_ids
          .map((id) => {
            const loc = findLocById(id)
            return loc ? formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (toLabels.length > 0) {
          searchParams.to_location = toLabels.join(",")
        }
      }

      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date
      if (formData.route_id) searchParams.route_id = formData.route_id

      const response = await mlSheetService.getBookedShipments(searchParams)
      const shipmentsData = response.data.shipments || response.data.data || response.data || []
      const selectedRoute = routes.find((r) => String(r.id) === String(formData.route_id))
      const corridor = filterShipmentsForMlDispatchForm(
        Array.isArray(shipmentsData) ? shipmentsData : [],
        {
          locations,
          fromIds: formData.from_location_ids,
          toIds: formData.to_location_ids,
          route: selectedRoute,
          parentMap: hubParentMap,
        },
      )
  
      // Filter out shipments that are already selected
      const newShipments = corridor.filter(
        (s) => !selectedShipmentIds.includes(s.shipment_id || s.id),
      )
  
      setFilteredShipments(newShipments)
      setSelectAllChecked(false)
    } catch (error) {
      console.error("Error searching shipments:", error)
      setFilteredShipments([])
      setSelectAllChecked(false)
    } finally {
      setSearchingShipments(false)
    }
  }

  const handleLocationToggle = (locationType, locationId) => {
    if (locationType === "from" && lockFromLocation) return
    setFormData((prev) => {
      const currentIds = prev[locationType === "from" ? "from_location_ids" : "to_location_ids"]
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)

      if (locationType === "from") {
        const nextFrom = alreadySelected
          ? currentIds.filter((id) => String(id) !== idStr)
          : [...currentIds, locationId]
        let nextTo = prev.to_location_ids
        const eligibleToIds = new Set(
          toLocationsForMlDispatch(locations, nextFrom, {
            enabled: hasInternalHubDispatchControls(user),
          }).map((loc) =>
            String(loc.id || loc.hub_id),
          ),
        )
        nextTo = prev.to_location_ids.filter((id) => eligibleToIds.has(String(id)))
        const allowedTo = new Set(nextTo.map(String))
        return {
          ...prev,
          from_location_ids: nextFrom,
          to_location_ids: nextTo,
          crossing_destination_hub_ids: prev.crossingStopsFromRoute
            ? (prev.crossing_destination_hub_ids || [])
            : (prev.crossing_destination_hub_ids || []).filter((id) =>
              allowedTo.has(String(id)),
            ),
        }
      }

      const nextTo = alreadySelected
        ? currentIds.filter((id) => String(id) !== idStr)
        : [...currentIds, locationId]
      const allowedTo = new Set(nextTo.map(String))
      return {
        ...prev,
        to_location_ids: nextTo,
        crossing_destination_hub_ids: prev.crossingStopsFromRoute
          ? (prev.crossing_destination_hub_ids || [])
          : (prev.crossing_destination_hub_ids || []).filter((id) =>
            allowedTo.has(String(id)),
          ),
      }
    })
  }

  const handleCrossingHubToggle = (locationId) => {
    setFormData((prev) => {
      const currentIds = prev.crossing_location_hub_ids || []
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)
      const nextIds = alreadySelected
        ? currentIds.filter((id) => String(id) !== idStr)
        : [...currentIds, locationId]
      const nextDestinations = nextIds.length ? prev.crossing_destination_hub_ids : []
      return {
        ...prev,
        crossing_location_hub_ids: nextIds,
        crossing_destination_hub_ids: nextDestinations,
        crossingStopsFromRoute: false,
        crossing_stops: crossingStopsFromFlat(nextIds, nextDestinations),
      }
    })
  }

  const handleCrossingDestinationToggle = (locationId) => {
    setCrossingDestUnset(false)
    setFormData((prev) => {
      const currentIds = prev.crossing_destination_hub_ids || []
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)
      const nextDestinations = alreadySelected
        ? currentIds.filter((id) => String(id) !== idStr)
        : [...currentIds, locationId]
      return {
        ...prev,
        crossing_destination_hub_ids: nextDestinations,
        crossingStopsFromRoute: false,
        crossing_stops: crossingStopsFromFlat(prev.crossing_location_hub_ids, nextDestinations),
      }
    })
  }

  const sameHubIdList = (left, right) => {
    const a = [...(left || [])].map(String).sort()
    const b = [...(right || [])].map(String).sort()
    return a.length === b.length && a.every((id, index) => id === b[index])
  }

  const toLocationsForDispatch = () =>
    toLocationsForMlDispatch(locations, formData.from_location_ids, {
      enabled: hasInternalHubDispatchControls(user),
    })

  const isLocationSelected = (selectedIds, locationId) =>
    selectedIds.some((id) => String(id) === String(locationId))

  const handleSearchInput = (dropdownType, value) => {
    setSearchInputs((prev) => ({ ...prev, [dropdownType]: value }))
  }

  const getFilteredLocations = (searchTerm) => {
    const q = (searchTerm || "").toLowerCase().trim()
    if (!q) return locations
    return locations.filter((loc) => {
      const label = formatHubBranchLabel(loc).toLowerCase()
      const locName = (loc.location || loc.hub_name || loc.name || "").toLowerCase()
      return label.includes(q) || locName.includes(q)
    })
  }

  const getFilteredToLocations = (searchTerm) => {
    const q = (searchTerm || "").toLowerCase().trim()
    return toLocationsForDispatch().filter((loc) => {
      if (!q) return true
      const label = formatHubBranchLabel(loc).toLowerCase()
      const locName = (loc.location || loc.hub_name || loc.name || "").toLowerCase()
      return label.includes(q) || locName.includes(q)
    })
  }

  const getFilteredVehicles = (searchTerm) => {
    const searchLower = (searchTerm || "").toLowerCase()
    return vehicles.filter((vehicle) => {
      const vehicleNumber = (vehicle.vehicle_number || "").toLowerCase()
      const vehicleType = (vehicle.vehicle_type || "").toLowerCase()
      const ownerName = (vehicle.owner_name || "").toLowerCase()
      return (
        vehicleNumber.includes(searchLower) ||
        vehicleType.includes(searchLower) ||
        ownerName.includes(searchLower)
      )
    })
  }

  const handleRouteSelect = (e) => {
    const { value } = e.target
    if (!value) {
      setFormData((prev) => ({ ...prev, route_id: "" }))
      return
    }
    const route = routes.find((r) => String(r.id) === String(value))
    if (!route) return
    setFormData((prev) =>
      applySelectedRouteToMlForm(route, prev, {
        locations,
        user,
        parentMap: hubParentMap,
      }),
    )
  }

  const accessAgentGroups = groupAccessAgents(
    agents,
    searchInputs.access,
    formData.to_location_ids,
    locations,
  )

  // When a new destination is added (Chennai sheet, then Delhi), select that hub's agent.
  // Locations already saved on the sheet are left untouched so an unchecked agent stays unchecked.
  useEffect(() => {
    if (syncedToLocationIdsRef.current == null) return
    if (!agents.length) return
    const current = formData.to_location_ids || []
    const synced = new Set(syncedToLocationIdsRef.current)
    const added = current.filter((id) => !synced.has(String(id)))
    if (!added.length) return
    const matchedIds = matchedAccessUserIds(
      agents,
      added,
      locations,
      user?.user_id,
    )
    syncedToLocationIdsRef.current = [
      ...syncedToLocationIdsRef.current,
      ...added.map(String),
    ]
    if (!matchedIds.length) return
    setFormData((prev) => {
      const seen = new Set()
      const merged = []
      for (const id of [...matchedIds, ...(prev.access_user_ids || [])]) {
        const key = String(id)
        if (seen.has(key) || key === String(user?.user_id || '')) continue
        seen.add(key)
        merged.push(id)
      }
      const same =
        merged.length === (prev.access_user_ids || []).length &&
        merged.every((id, index) => String(id) === String(prev.access_user_ids[index]))
      if (same) return prev
      return { ...prev, access_user_ids: merged }
    })
  }, [formData.to_location_ids, agents, locations, user?.user_id])

  const renderAccessAgentOption = (agent) => {
    const agentId = agent.user_id || agent.id
    const meta = formatAccessAgentMeta(agent)
    return (
      <label key={agentId} className={ML_MOBILE_DROPDOWN_ITEM}>
        <input
          type="checkbox"
          checked={formData.access_user_ids.includes(agentId)}
          onChange={() => handleAccessUserChange(agentId)}
          className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0"
        />
        <div className="min-w-0">
          <p className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-semibold text-secondary`}>{agent.name}</p>
          {meta && <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 break-words">{meta}</p>}
        </div>
      </label>
    )
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSelectShipment = (shipmentId) => {
    setSelectedShipmentIds((prev) =>
      prev.includes(shipmentId) ? prev.filter((id) => id !== shipmentId) : [...prev, shipmentId],
    )
  }

  const getCombinedShipments = () => {
    const shipmentMap = new Map()

    allShipments.forEach((s) => {
      const id = s.shipment_id || s.id
      if (id) {
        shipmentMap.set(id, { ...s, isFromMLSheet: true })
      }
    })

    filteredShipments.forEach((s) => {
      const id = s.shipment_id || s.id
      if (id && !shipmentMap.has(id)) {
        shipmentMap.set(id, { ...s, isFromMLSheet: false })
      }
    })

    return Array.from(shipmentMap.values())
  }

  const combinedShipments = getCombinedShipments()
  const hasShipments = selectedShipmentIds.length > 0 || combinedShipments.length > 0

  useEffect(() => {
    if (combinedShipments.length === 0) {
      setSelectAllChecked(false)
      return
    }
    setSelectAllChecked(
      combinedShipments.every((s) => selectedShipmentIds.includes(s.shipment_id || s.id)),
    )
  }, [selectedShipmentIds, combinedShipments])

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedShipmentIds([])
    } else {
      const allIds = combinedShipments.map((s) => s.shipment_id || s.id).filter(Boolean)
      setSelectedShipmentIds(allIds)
    }
  }

  const handleVehicleChange = (e) => {
    const { value } = e.target
    const selectedVehicle = vehicles.find((v) => v.id === Number.parseInt(value) || v.vehicle_id === value)
    setFormData({
      ...formData,
      vehicle_id: selectedVehicle?.id || selectedVehicle?.vehicle_id || value,
      driver_contact: sanitizeDriverContact(selectedVehicle?.driver_contact),
    })
  }

  const handleVehicleCreated = (createdVehicle) => {
    const newVehicleId = createdVehicle.id || createdVehicle.vehicle_id
    setVehicles((prev) => [...prev, createdVehicle])
    setFormData((prev) => ({
      ...prev,
      vehicle_id: newVehicleId,
      driver_contact: sanitizeDriverContact(createdVehicle.driver_contact) || prev.driver_contact,
    }))
    setOpenDropdown(null)
    setSearchInputs((prev) => ({ ...prev, vehicle: "" }))
    setShowVehicleModal(false)
  }

  const openVehicleModal = (prefillNumber = "") => {
    setPendingVehicleSearch(prefillNumber || searchInputs.vehicle)
    setShowVehicleModal(true)
    setOpenDropdown(null)
  }

  const selectedVehicleForDisplay = vehicles.find(
    (v) => (v.id || v.vehicle_id) === formData.vehicle_id
  )

  // Cargo overweight check for the current selection vs the chosen vehicle.
  const isCargoMode = !!user?.is_cargo_shipment
  const selectedShipmentObjs = combinedShipments.filter((s) =>
    selectedShipmentIds.includes(s.shipment_id || s.id)
  )
  const locationChipCounts = mlLocationChipCounts(selectedShipmentObjs, {
    parentMap: hubParentMap,
    toIds: formData.to_location_ids,
    crossingStops: formData.crossing_stops,
    locations,
  })
  const locationChipCountsRef = useRef(locationChipCounts)
  locationChipCountsRef.current = locationChipCounts
  const locationCountKey = Object.entries(locationChipCounts)
    .map(([id, count]) => `${id}:${count}`)
    .sort()
    .join('|')

  useEffect(() => {
    if (searchingShipments || !selectedShipmentObjs.length) return
    setFormData((prev) => dropDispatchLocationsWithoutShipments(prev, locationChipCountsRef.current))
  }, [locationCountKey, searchingShipments, selectedShipmentObjs.length])
  const load = evaluateVehicleLoad({
    vehicle: selectedVehicleForDisplay,
    shipments: selectedShipmentObjs,
    alreadyLoadedKg,
  })
  const isOverweight = isCargoMode && load.hasCapacity && load.overweight

  const handleRemoveExtraShipments = () => {
    if (!load.extraShipmentIds.length) return
    setSelectedShipmentIds((prev) =>
      prev.filter((id) => !load.extraShipmentIds.includes(id))
    )
    setSelectAllChecked(false)
  }

  const handleDriverContactChange = (e) => {
    setFormData({
      ...formData,
      driver_contact: sanitizeDriverContact(e.target.value),
    })
  }

  const handleAccessUserChange = (userId) => {
    setFormData((prev) => ({
      ...prev,
      access_user_ids: prev.access_user_ids.includes(userId)
        ? prev.access_user_ids.filter((id) => id !== userId)
        : [...prev.access_user_ids, userId],
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isMobileLayout && mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }

    if (formData.from_location_ids.length === 0) {
      Alert.error("Please select at least one from location")
      return
    }

    if (formData.to_location_ids.length === 0) {
      Alert.error("Please select at least one to location")
      return
    }

    if (!formData.route_id) {
      Alert.error("Please select a route (or wait for auto-match after From/To are set)")
      return
    }

    if (routeLocationWarning) {
      Alert.error(routeLocationWarning)
      return
    }

    if (!formData.vehicle_id) {
      Alert.error("Please select a vehicle")
      return
    }

    if (!isValidDriverContact(formData.driver_contact)) {
      Alert.error("Driver contact must be 10 digits")
      return
    }

    if (selectedShipmentIds.length === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (accessUsersRequired && formData.access_user_ids.length === 0) {
      Alert.error("Please select at least one access user")
      return
    }

    if (accessUsersRequired && formData.access_user_ids.includes(user?.user_id)) {
      Alert.error("You cannot assign the ML sheet to yourself. Select another access user.")
      return
    }

    const crossingSelected = canSelectCrossingHubs && formData.crossing_location_hub_ids.length > 0
    const keepUnsetDestinations =
      crossingDestUnset &&
      formData.crossing_destination_hub_ids.length === 0 &&
      sameHubIdList(formData.crossing_location_hub_ids, savedCrossingHubIds)
    if (crossingSelected && formData.crossing_destination_hub_ids.length === 0 && !keepUnsetDestinations) {
      Alert.error("Select the locations that need to be crossed")
      return
    }

    if (isOverweight) {
      Alert.error(
        `Vehicle will be overweight by ${formatKg(load.excessKg)} ` +
          `(new ${formatKg(load.newWeight)} + already loaded that day ${formatKg(load.alreadyLoadedKg)} ` +
          `> capacity ${formatKg(load.capacityKg)}; available ${formatKg(load.availableKg)}). ` +
          `Remove some shipments or use another vehicle.`
      )
      return
    }

    try {
      setSubmitting(true)

      const payload = {
        from_location_ids: formData.from_location_ids,
        to_location_ids: formData.to_location_ids,
        crossing_location_hub_ids: canSelectCrossingHubs ? formData.crossing_location_hub_ids : [],
        crossing_destination_hub_ids:
          canSelectCrossingHubs && formData.crossing_location_hub_ids.length > 0
            ? (formData.crossing_destination_hub_ids.length > 0
              ? formData.crossing_destination_hub_ids
              : null)
            : null,
        vehicle_id: formData.vehicle_id,
        driver_contact: formData.driver_contact,
        shipment_ids: selectedShipmentIds,
        access_user_ids: formData.access_user_ids,
        route_id: formData.route_id || null,
        crossing_stops: canSelectCrossingHubs
          ? (formData.crossingStopsFromRoute
            ? (formData.crossing_stops || []).map((stop) => ({
              hub_id: Number(stop.hub_id),
              destination_hub_ids: (stop.destination_hub_ids || []).map((id) => Number(id)),
            }))
            : (keepUnsetDestinations
              ? undefined
              : crossingStopsFromFlat(
                formData.crossing_location_hub_ids,
                formData.crossing_destination_hub_ids,
              )))
          : [],
      }

      const response = await mlSheetService.updateMLSheet(mlSheetId, payload)
      showMlSheetAutoSyncAlerts(response, { baseSuccess: 'ML Sheet updated successfully' })
      navigate("/ml-sheets")
    } catch (error) {
      console.error("Error updating ML Sheet:", error)
      if (error.response?.status === 409) {
        const blockers = error.response?.data?.edit_blockers || []
        Alert.error(blockers[0]?.message || error.response?.data?.error || "This ML sheet is locked after payment. Create a new ML sheet.")
      } else {
        Alert.error(error.response?.data?.error || "Failed to update ML Sheet")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  return (
    <DashboardLayout fullWidth fillViewport={isMobileLayout}>
      {(loading || !loadedSelectedShipmentData) ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading ML Sheet..." />
        </div>
      ) : (
        <>
                <form
                  id="edit-ml-sheet-form"
                  noValidate={isMobileLayout}
                  onSubmit={handleSubmit}
                  className={`min-w-0 flex flex-col md:[&_input]:py-1.5 md:[&_select]:py-1.5 ${isMobileLayout ? "flex-1 min-h-0 overflow-hidden pb-20" : "space-y-3 sm:space-y-4"}`}
                >
                {isMobileLayout && (
                  <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-1 mb-2">
                      {MOBILE_STEPS.map((step, idx) => (
                        <button
                          key={step.id}
                          type="button"
                          onClick={() => handleMobileStepTabClick(idx)}
                          className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                            idx === mobileStep
                              ? "bg-orange-500 text-white shadow-sm"
                              : idx < mobileStep
                                ? "bg-orange-100 text-orange-700"
                                : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {step.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <h1 className="flex items-center gap-1.5 text-sm font-bold text-gray-700 truncate">
                        <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                        <span className="truncate">{MOBILE_STEPS[mobileStep].label}</span>
                      </h1>
                      <Button
                        variant="secondary"
                        type="button"
                        size="sm"
                        icon={XCircle}
                        mobileIconOnly
                        mobileLabel="Cancel"
                        onClick={() => navigate("/ml-sheets")}
                      />
                    </div>
                  </div>
                )}

                <div className="hidden md:block shrink-0">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <h1 className="text-lg sm:text-2xl font-bold text-gray-700">
                      {t('pages.mlSheet.edit', { defaultValue: 'Edit ML Sheet' })}
                      {mlSheet?.ml_sheet_id ? ` #${mlSheet.ml_sheet_id}` : ''}
                    </h1>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button variant="primary" onClick={handleSubmit} disabled={submitting || isOverweight} loading={submitting} className="w-full sm:w-auto">
                        {submitting ? "Updating..." : "Update"}
                      </Button>
                      <Button variant="secondary" type="button" onClick={() => navigate("/ml-sheets")} className="w-full sm:w-auto">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>

                <div className={isMobileLayout ? "contents" : "space-y-3 sm:space-y-4"}>
                      {/* Location & Date Filters */}
                  <div className={isMobileLayout ? "contents" : "grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4"}>
                    <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 ${mobileStepPanelClass(0)} md:!block`}>
                      <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3 sm:mb-4">Location & Date Filters</h3>

                      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div className={`relative min-w-0 ${fromLocationLocked ? "opacity-60 pointer-events-none" : ""}`} ref={fromRef}>
                          <label className={ML_MOBILE_FIELD_LABEL}>
                            From Locations <span className="text-red-500 ml-1">*</span>
                            {lockFromLocation && (
                              <span className="ml-1 text-[10px] font-normal text-gray-500">(locked to your hub)</span>
                            )}
                          </label>
                          <div
                            onClick={() => {
                              if (fromLocationLocked) return
                              setOpenDropdown(openDropdown === "from" ? null : "from")
                            }}
                            className={mlMobileTriggerClass(openDropdown === "from")}
                          >
                            <span className={ML_MOBILE_TRIGGER_TEXT}>
                              {formData.from_location_ids.length > 0
                                ? `${formData.from_location_ids.length} selected`
                                : "Select locations"}
                            </span>
                            <span
                              className={`shrink-0 text-[10px] sm:text-sm transition ${
                                openDropdown === "from" ? "rotate-180 text-blue-500" : "text-gray-500"
                              }`}
                            >
                              ▾
                            </span>
                          </div>

                          {openDropdown === "from" && !fromLocationLocked && (
                            <div className={ML_MOBILE_DROPDOWN_PANEL}>
                              <input
                                type="text"
                                placeholder="Search locations..."
                                value={searchInputs.from}
                                onChange={(e) => handleSearchInput('from', e.target.value)}
                                autoFocus
                                className={ML_MOBILE_DROPDOWN_SEARCH}
                              />
                              <div className="max-h-48 overflow-y-auto">
                                {getFilteredLocations(searchInputs.from).map((loc) => {
                                  const locId = loc.id || loc.hub_id
                                  return (
                                    <label
                                      key={locId}
                                      className={ML_MOBILE_DROPDOWN_ITEM}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isLocationSelected(formData.from_location_ids, locId)}
                                        onChange={() => handleLocationToggle("from", locId)}
                                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                      />
                                      <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                        {formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          <div className="mt-1 min-w-0">
                          <MlLocationChips
                            compact
                            locations={locations}
                            selectedIds={formData.from_location_ids}
                            onRemove={fromLocationLocked ? undefined : (id) => handleLocationToggle("from", id)}
                          />
                          </div>
                        </div>

                        <div className="relative min-w-0" ref={toRef}>
                          <label className={ML_MOBILE_FIELD_LABEL}>
                            To Locations <span className="text-red-500 ml-1">*</span>
                          </label>

                          <div
                            onClick={() =>
                              setOpenDropdown(openDropdown === "to" ? null : "to")
                            }
                            className={mlMobileTriggerClass(openDropdown === "to")}
                          >
                            <span className={ML_MOBILE_TRIGGER_TEXT}>
                              {formData.to_location_ids.length > 0
                                ? `${formData.to_location_ids.length} selected`
                                : "Select locations"}
                            </span>

                            <span
                              className={`shrink-0 text-[10px] sm:text-sm transition ${
                                openDropdown === "to" ? "rotate-180 text-blue-500" : "text-gray-500"
                              }`}
                            >
                              ▾
                            </span>
                          </div>

                          {openDropdown === "to" && (
                            <div className={ML_MOBILE_DROPDOWN_PANEL}>
                              <input
                                type="text"
                                placeholder="Search locations..."
                                value={searchInputs.to}
                                onChange={(e) => handleSearchInput('to', e.target.value)}
                                autoFocus
                                className={ML_MOBILE_DROPDOWN_SEARCH}
                              />
                              <div className="max-h-48 overflow-y-auto">
                                {Array.isArray(locations) && getFilteredToLocations(searchInputs.to).length > 0 ? (
                                  getFilteredToLocations(searchInputs.to).map((loc) => {
                                    const locId = loc.id || loc.hub_id
                                    return (
                                      <label
                                        key={locId}
                                        className={ML_MOBILE_DROPDOWN_ITEM}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isLocationSelected(formData.to_location_ids, locId)}
                                          onChange={() => handleLocationToggle("to", locId)}
                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                        />
                                        <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                          {formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name}
                                        </span>
                                      </label>
                                    )
                                  })
                                ) : (
                                  <p className="text-[10px] sm:text-sm text-gray-500 p-2">
                                    {formData.from_location_ids.length === 0
                                      ? "Select From location first"
                                      : "No destination hubs configured for this From location"}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-1 min-w-0">
                          <MlLocationChips
                            compact
                            locations={locations}
                            selectedIds={formData.to_location_ids}
                            counts={locationChipCounts}
                            onRemove={(id) => handleLocationToggle("to", id)}
                          />
                          </div>
                        </div>

                        <MlRouteSelectField
                          className={canSelectCrossingHubs ? "" : "col-span-2"}
                          panelRef={routeRef}
                          routes={routes}
                          fromIds={formData.from_location_ids}
                          toIds={formData.to_location_ids}
                          routeId={formData.route_id}
                          parentMap={hubParentMap}
                          locationMismatchWarning={routeLocationWarning}
                          required
                          open={openDropdown === "route"}
                          onOpenChange={(next) => setOpenDropdown(next ? "route" : null)}
                          searchTerm={searchInputs.route}
                          onSearchChange={(value) => handleSearchInput("route", value)}
                          onSelectCustom={() => handleRouteSelect({ target: { value: "" } })}
                          onSelectRoute={(route) =>
                            handleRouteSelect({ target: { value: route.id } })
                          }
                        />

                        {canSelectCrossingHubs && formData.crossingStopsFromRoute && formData.crossing_stops.length > 0 && (
                          <p className="sm:col-span-2 text-[11px] sm:text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                            Crossing from this route: {formatCrossingStopSummary(formData.crossing_stops)}
                          </p>
                        )}

                        {canSelectCrossingHubs && (
                          <div className="relative min-w-0" ref={crossingRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>
                              Crossing Location Hubs
                            </label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "crossing" ? null : "crossing")
                              }
                              className={mlMobileTriggerClass(openDropdown === "crossing")}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.crossing_location_hub_ids.length > 0
                                  ? `${formData.crossing_location_hub_ids.length} selected`
                                  : "Select location hubs"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "crossing" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>

                            {openDropdown === "crossing" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search location hubs..."
                                  value={searchInputs.crossing}
                                  onChange={(e) => handleSearchInput("crossing", e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-48 overflow-y-auto">
                                  {getFilteredLocations(searchInputs.crossing).map((loc) => {
                                    const locId = loc.id || loc.hub_id
                                    return (
                                      <label
                                        key={locId}
                                        className={ML_MOBILE_DROPDOWN_ITEM}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isLocationSelected(formData.crossing_location_hub_ids, locId)}
                                          onChange={() => handleCrossingHubToggle(locId)}
                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                        />
                                        <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                          {formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name}
                                        </span>
                                      </label>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                            <div className="mt-1 min-w-0">
                            <MlLocationChips
                              compact
                              locations={locations}
                              selectedIds={formData.crossing_location_hub_ids}
                              counts={locationChipCounts}
                              onRemove={handleCrossingHubToggle}
                            />
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1 break-words">
                              Where the vehicle is crossed. This hub does not see this dispatch.
                            </p>
                          </div>
                        )}

                        {canSelectCrossingHubs && formData.crossing_location_hub_ids.length > 0 && (
                          <div className="relative min-w-0" ref={crossingDestRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>
                              Locations To Cross <span className="text-red-500">*</span>
                            </label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "crossingDest" ? null : "crossingDest")
                              }
                              className={mlMobileTriggerClass(openDropdown === "crossingDest")}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.crossing_destination_hub_ids.length > 0
                                  ? `${formData.crossing_destination_hub_ids.length} selected`
                                  : "Select To locations"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "crossingDest" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>

                            {openDropdown === "crossingDest" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search To locations..."
                                  value={searchInputs.crossingDest || ""}
                                  onChange={(e) => handleSearchInput("crossingDest", e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-48 overflow-y-auto">
                                  {getFilteredLocations(searchInputs.crossingDest || "")
                                    .filter((loc) =>
                                      formData.to_location_ids.some(
                                        (id) => String(id) === String(loc.id || loc.hub_id),
                                      ),
                                    )
                                    .map((loc) => {
                                      const locId = loc.id || loc.hub_id
                                      return (
                                        <label key={locId} className={ML_MOBILE_DROPDOWN_ITEM}>
                                          <input
                                            type="checkbox"
                                            checked={isLocationSelected(formData.crossing_destination_hub_ids, locId)}
                                            onChange={() => handleCrossingDestinationToggle(locId)}
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                          />
                                          <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                            {formatHubBranchLabel(loc) || loc.location || loc.hub_name || loc.name}
                                          </span>
                                        </label>
                                      )
                                    })}
                                </div>
                              </div>
                            )}
                            <div className="mt-1 min-w-0">
                              <MlLocationChips
                                compact
                                locations={locations}
                                selectedIds={formData.crossing_destination_hub_ids}
                                counts={locationChipCounts}
                                onRemove={handleCrossingDestinationToggle}
                              />
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1 break-words">
                              Only shipments to these locations show on the crossing page.
                            </p>
                          </div>
                        )}

                        <div className="min-w-0">
                          <label className={ML_MOBILE_FIELD_LABEL}>From Date</label>
                          <input
                            type="date"
                            name="from_date"
                            value={formData.from_date}
                            onChange={handleDateChange}
                            className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={ML_MOBILE_FIELD_LABEL}>To Date</label>
                          <input
                            type="date"
                            name="to_date"
                            value={formData.to_date}
                            onChange={handleDateChange}
                            className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                          />
                        </div>

                      </div>
                    </div>
                    <div className={isMobileLayout ? "contents" : "grid grid-cols-1 gap-3 sm:gap-4 min-w-0"}>
                      {/* Vehicle & Driver Info */}
                      <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 ${mobileStepPanelClass(1)} md:!block`}>
                        <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3 sm:mb-4">Vehicle & Driver Information</h3>
                        <div className="space-y-3 sm:space-y-4">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <div className="relative min-w-0" ref={vehicleRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>Vehicle <span className="text-red-500 ml-1">*</span></label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "vehicle" ? null : "vehicle")
                              }
                              className={mlMobileTriggerClass(openDropdown === "vehicle")}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.vehicle_id
                                  ? (() => {
                                    const vehicle = vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                                    return vehicle ? formatVehicleLabel(vehicle) : "Select a vehicle"
                                  })()
                                  : "Select a vehicle"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "vehicle" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>

                            {openDropdown === "vehicle" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search vehicles..."
                                  value={searchInputs.vehicle}
                                  onChange={(e) => handleSearchInput('vehicle', e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-48 overflow-y-auto">
                                  {getFilteredVehicles(searchInputs.vehicle).length > 0 ? (
                                    getFilteredVehicles(searchInputs.vehicle).map((v) => {
                                      const vehicleId = v.id || v.vehicle_id
                                      return (
                                        <div
                                          key={vehicleId}
                                          onClick={() => {
                                            handleVehicleChange({target: {value: vehicleId}})
                                            setOpenDropdown(null)
                                            setSearchInputs(prev => ({...prev, vehicle: ""}))
                                          }}
                                          className={`${ML_MOBILE_DROPDOWN_ITEM} border-b border-gray-100 last:border-b-0`}
                                        >
                                          <span className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-medium text-gray-800`}>
                                            {formatVehicleLabel(v)}
                                          </span>
                                        </div>
                                      )
                                    })
                                  ) : (
                                    <p className="text-[10px] sm:text-sm text-gray-500 p-2">No vehicles found</p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openVehicleModal(searchInputs.vehicle)}
                                  className="w-full flex items-center justify-center gap-2 px-2 py-1.5 mt-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-[10px] sm:text-sm font-medium"
                                >
                                  <Plus size={14} />
                                  Add New Vehicle
                                </button>
                              </div>
                            )}
                            <CreateVehicleWithOwnerModal
                              open={showVehicleModal}
                              onClose={() => setShowVehicleModal(false)}
                              onSuccess={handleVehicleCreated}
                              initialVehicleNumber={pendingVehicleSearch}
                              companyAccess={companyAccess}
                            />
                            {(() => {
                              const sel = vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                              return sel?.owner_name ? (
                                <p className="text-xs text-gray-600 mt-1">
                                  Vehicle owner: <span className="font-medium">{sel.owner_name}</span>
                                </p>
                              ) : null
                            })()}
                          </div>
                          <div className="min-w-0">
                            <label className={ML_MOBILE_FIELD_LABEL}>Driver Contact <span className="text-red-500">*</span></label>
                            <input
                              type="tel"
                              inputMode="numeric"
                              maxLength={10}
                              value={formData.driver_contact}
                              onChange={handleDriverContactChange}
                              placeholder="10 digit contact"
                              className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                            />
                            {formData.driver_contact.length > 0 && formData.driver_contact.length !== 10 && (
                              <p className="text-[10px] sm:text-xs text-red-500 mt-1">Driver contact is required (10 digits)</p>
                            )}
                          </div>
                        </div>
                          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                          <div className="relative w-full sm:w-56 lg:w-64 shrink-0" ref={accessRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>
                              ML Access Agents{' '}
                              {accessUsersRequired ? (
                                <span className="text-red-500">*</span>
                              ) : (
                                <span className="text-gray-400 font-normal">(optional)</span>
                              )}
                            </label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "access" ? null : "access")
                              }
                              className={`w-full ${mlMobileTriggerClass(openDropdown === "access")}`}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.access_user_ids.length > 0
                                  ? `${formData.access_user_ids.length} selected`
                                  : "Select users"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "access" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>
                            {openDropdown === "access" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search by name, location, contact..."
                                  value={searchInputs.access}
                                  onChange={(e) => handleSearchInput('access', e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-56 overflow-y-auto">
                                  {Array.isArray(agents) && accessAgentGroups.all.length > 0 ? (
                                    <>
                                      {accessAgentGroups.hasToFilter && accessAgentGroups.matched.length > 0 && (
                                        <>
                                          <p className="text-[11px] font-semibold text-indigo-700 px-2 py-1 sticky top-0 bg-white">
                                            Agents at destination
                                          </p>
                                          {accessAgentGroups.matched.map(renderAccessAgentOption)}
                                        </>
                                      )}
                                      {accessAgentGroups.hasToFilter && accessAgentGroups.others.length > 0 && (
                                        <>
                                          <p className="text-[11px] font-semibold text-gray-500 px-2 py-1 mt-1 border-t border-gray-100 sticky top-0 bg-white">
                                            Other agents
                                          </p>
                                          {accessAgentGroups.others.map(renderAccessAgentOption)}
                                        </>
                                      )}
                                      {!accessAgentGroups.hasToFilter &&
                                        accessAgentGroups.all.map(renderAccessAgentOption)}
                                    </>
                                  ) : (
                                    <p className="text-sm text-gray-500 p-2">
                                      {!agents.length
                                        ? 'No users available'
                                        : searchInputs.access.trim()
                                        ? 'No users match your search'
                                        : 'No users available'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 sm:pt-6">
                            <MlAccessAgentChips
                              agents={agents}
                              selectedIds={formData.access_user_ids}
                              onRemove={handleAccessUserChange}
                            />
                          </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Shipments Selection */}
                  <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 max-w-full ${mobileStepPanelClass(2)} md:!block`}>
                    <div className="flex flex-wrap items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3 shrink-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
                        <h3 className="font-semibold text-secondary text-sm sm:text-base whitespace-nowrap">
                          Select Shipments ({selectedShipmentIds.length}/{combinedShipments.length})
                        </h3>
                        {searchingShipments && <span className="text-xs text-blue-600">Searching...</span>}

                        {isCargoMode && load.hasCapacity && selectedShipmentIds.length > 0 && (
                          <span
                            title={
                              `This sheet ${formatKg(load.newWeight)} + other MLs that day ${formatKg(load.alreadyLoadedKg)} ` +
                              `= ${formatKg(load.totalWeight)} / capacity ${formatKg(load.capacityKg)} ` +
                              `(available ${formatKg(load.availableKg)})` +
                              (isOverweight ? ` — over by ${formatKg(load.excessKg)}` : "")
                            }
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] sm:text-xs font-medium ${
                              isOverweight
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-green-200 bg-green-50 text-green-700"
                            }`}
                          >
                            {isOverweight ? (
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            )}
                            <span className="whitespace-nowrap">
                              <span className="font-semibold">
                                {isOverweight ? "Overweight" : "Within capacity"}
                              </span>
                              <span className="hidden sm:inline opacity-70">
                                {" · "}{formatKg(load.newWeight)}+{formatKg(load.alreadyLoadedKg)}/
                                {formatKg(load.capacityKg)}
                                {isOverweight ? ` (+${formatKg(load.excessKg)})` : ""}
                              </span>
                            </span>
                            {isOverweight && load.extraShipmentIds.length > 0 && (
                              <button
                                type="button"
                                onClick={handleRemoveExtraShipments}
                                className="ml-0.5 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] sm:text-[11px] font-semibold text-white hover:bg-red-700 whitespace-nowrap"
                              >
                                Remove {load.extraShipmentIds.length}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {hasShipments && (
                        <>
                          <label className="flex xl:hidden items-center gap-2.5 cursor-pointer py-1 shrink-0">
                            <input
                              type="checkbox"
                              checked={selectAllChecked}
                              onChange={handleSelectAll}
                              className="w-5 h-5 flex-shrink-0 accent-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {selectAllChecked ? "Deselect All" : "Select All"}
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={handleSelectAll}
                            className="hidden xl:inline-flex w-auto px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold text-sm shrink-0"
                          >
                            {selectAllChecked ? "Deselect All" : "Select All"}
                          </button>
                        </>
                      )}
                    </div>

                    <div className={`min-w-0 max-w-full ${isMobileLayout ? "flex-1 min-h-0 overflow-y-auto overscroll-contain" : "overflow-x-auto"}`}>
                      {searchingShipments ? (
                        <Loader fullScreen={false} size="small" message="Searching for shipments..." />
                      ) : (
                        <MLSheetShipmentRecordList
                          shipments={combinedShipments}
                          selectedShipmentIds={selectedShipmentIds}
                          onToggle={handleSelectShipment}
                          onSelectAll={handleSelectAll}
                          selectAllChecked={selectAllChecked}
                          onView={(shipmentId) => navigate(`/shipments/view/${shipmentId}`)}
                          getStatusColor={getShipmentStatusColor}
                          emptyMessage={
                            !formData.from_location_ids.length || !formData.to_location_ids.length
                              ? "Select both From and To locations to load shipments"
                              : !formData.route_id
                                ? "Select a route (auto-matched from From/To, or pick manually) to load shipments"
                                : "No booked shipments match this From, To, and route"
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
                </form>

                {isMobileLayout && (
                  <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
                    {mobileStep > 0 ? (
                      <Button variant="secondary" type="button" icon={ChevronLeft} mobileIconOnly mobileLabel="Back" onClick={goToPrevMobileStep} />
                    ) : (
                      <div className="w-10" />
                    )}
                    <Button
                      variant="primary"
                      type="button"
                      icon={mobileStep < MOBILE_STEPS.length - 1 ? ChevronRight : undefined}
                      iconPosition="right"
                      onClick={handleMobilePrimaryAction}
                      loading={submitting && mobileStep >= MOBILE_STEPS.length - 1}
                      disabled={submitting || (mobileStep >= MOBILE_STEPS.length - 1 && isOverweight)}
                    >
                      {mobileStep < MOBILE_STEPS.length - 1
                        ? "Next"
                        : submitting
                          ? "Updating..."
                          : "Update"}
                    </Button>
                  </div>
                )}
        </>
      )}
    </DashboardLayout>
  )
}

export default EditMLSheet;